/**
 * Backend refusal keys the frontend branches on, not just translates.
 *
 * This is NOT a mirror of a single backend catalog — `ProductStockErrorKeys` values are a
 * subset of `Beetegre.Domain/Products/ProductErrorKeys.cs`'s `product.stock.*` constants, and
 * `AuthActivateErrorKeys` values are a subset of the `auth.activate.*` constants in
 * `Beetegre.Domain/Auth/AuthErrorKeys.cs`.
 * The subset frontend branches on here is kept honest mechanically by
 * `tests/Beetegre.UnitTests/Ci/ApiErrorKeysParityTests.cs`, which reads this file and asserts
 * every value still exists on the backend side — a rename on either side breaks that test
 * instead of silently drifting.
 *
 * Lives under `types/` (not `lib/`) because the backend build-validation policy's path filter
 * covers `frontend/beetegre-ui/src/types/*` but not `.../src/lib/*` in general — see
 * `BuildValidationPathFilterTests` and the filter comment in `azure-pipelines-backend-ci.yaml`.
 */

export const ProductStockErrorKeys = {
  /** Shrinking the default warehouse below zero while other warehouses still hold stock. */
  InsufficientDefaultWarehouse: 'product.stock.insufficientDefaultWarehouse',
  /** Tenant has no default warehouse to absorb the stock delta. */
  NoDefaultWarehouse: 'product.stock.noDefaultWarehouse',
} as const;

export const AuthActivateErrorKeys = {
  /** The activation token existed but its expiry has passed — `canResend` accompanies this one. */
  TokenExpired: 'auth.activate.tokenExpired',
  /** The activation token does not resolve to a pending consume, or the user no longer exists. */
  TokenInvalid: 'auth.activate.tokenInvalid',
} as const;

/**
 * Backend refuses a billing purchase/top-up with 422 when the tenant has no valid phone on file
 * (AB#2193). `useBillingPurchaseWithPhoneGate` treats either key as the phone-required gate —
 * the subscription-purchase and credit-top-up endpoints raise their own key so a rejection can
 * still be traced to which flow triggered it server-side.
 */
export const BILLING_PHONE_ERROR_KEYS = {
  SubscriptionPhoneRequired: 'billing.subscription.phoneRequired',
  CreditPhoneRequired: 'billing.credit.phoneRequired',
} as const;

/**
 * "stop and ask" VAT-rate-conflict flow. `FinalizeVatRateConflict` is read from the
 * finalize 400's `message` to decide whether to open `OrderVatConflictDialog` from the response's
 * `vatRateConflicts` extension (the stale-detail path); `Stale` marks a 409 that means the
 * order's data changed since the dialog opened and the user must refresh.
 */
export const OrderVatConflictErrorKeys = {
  FinalizeVatRateConflict: 'order.validation.finalize.vatRateConflict',
  Stale: 'order.vatConflict.stale',
} as const;

/**
 * Finalize refuses with this key when the recomputed invoice total doesn't match what the
 * order already collected — `OrdersPage` re-renders the sentence with its own currency
 * formatter (the raw params carry unformatted decimals) instead of the backend's bare number.
 */
export const OrderFinalizeErrorKeys = {
  AmountMismatch: 'order.validation.finalize.amountMismatch',
  /** The 400 body's top-level `unresolved` lists every unresolved line (precheck item shape). */
  WarehouseRequired: 'order.validation.finalize.warehouseRequired',
  /** A chosen warehouse stopped being valid (stock moved); the dialog re-runs the precheck. */
  WarehouseInvalid: 'order.validation.finalize.warehouseInvalid',
} as const;

/**
 * e-Fatura convert refuses with 402 when billing is enforced and the tenant's credit balance is
 * below one submission. `InvoiceDetailDrawer` branches on it to show the localized shortfall
 * message with a link to the billing page.
 */
export const EFaturaConvertErrorKeys = {
  InsufficientCredit: 'efatura.convert.insufficient_credit',
  /** 502 whose `message` says Turkcell rejected our credentials, not that the service is down. */
  Unauthorized: 'efatura.error.unauthorized',
  /** 422 raised by our own pre-send consistency check: nothing reached Turkcell. */
  PayloadInvariant: 'efatura.error.payload_invariant',
} as const;

/**
 * The test-mail send refuses with this key when the tenant has no active email provider
 * settings. `TenantEmailTemplatesPage` branches on it to show an inline alert linking to the
 * email settings page instead of a toast.
 */
export const EmailTemplateErrorKeys = {
  ProviderSettingsMissing: 'emailTemplates.errors.providerSettingsMissing',
} as const;

/**
 * Logo upload refuses with 409 when the tenant has no active storage binding.
 * `isStorageNotConfigured` branches on it to show the "contact your administrator" state
 * instead of a generic upload error.
 */
export const CompanyErrorKeys = {
  LogoStorageNotConfigured: 'settings.company.logo.storage.notConfigured',
} as const;

/**
 * Warehouse delete refuses with 409 while the warehouse is still mapped to marketplace stores;
 * `WarehousesPage` renders the sentence with the `mappedStoreCount` from the body.
 */
export const WarehouseDeleteErrorKeys = {
  MarketplaceStoreMappingInUse: 'warehouse.validation.marketplaceStoreMapping.inUse',
} as const;

/**
 * Stock refusal raised while an invoice is approved. The bulk-invoice job report carries it as
 * structured params, and `JobResultDrawer` renders those params through this key.
 */
export const InvoiceStockErrorKeys = {
  StockInsufficient: 'invoice.validation.stock.insufficient',
} as const;
